import React, { useState, useEffect } from "react";
import "@/App.css";
import { 
  BookOpen, 
  Sparkles, 
  Brain, 
  Award, 
  Phone, 
  Calendar, 
  HeartHandshake, 
  CheckCircle2, 
  User, 
  ChevronRight, 
  HelpCircle,
  Clock,
  X,
  FileText,
  Settings,
  MessageSquare,
  Volume2
} from "lucide-react";
import { Toaster, toast } from "sonner";

export const Home = () => {
  // Global booking & config states
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [googleFormUrl, setGoogleFormUrl] = useState(() => {
    return localStorage.getItem("scionfocus_cta_url") || "https://forms.gle/miYFVAezanQYcnz26";
  });
  const [useGoogleForm, setUseGoogleForm] = useState(() => {
    const stored = localStorage.getItem("scionfocus_use_google_form");
    return stored !== null ? stored === "true" : true;
  });
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  // Diagnostic Quiz State
  const [quizStep, setQuizStateStep] = useState(0);
  const [quizName, setQuizName] = useState("");
  const [quizAge, setQuizAge] = useState("");
  const [quizAnswers, setQuizAnswers] = useState({
    reading: "",
    math: "",
    focus: "",
    speech: ""
  });
  const [quizResult, setQuizResult] = useState(null);

  // Form Booking State
  const [bookingForm, setBookingForm] = useState({
    childName: "",
    childAge: "",
    parentName: "",
    phone: "",
    challenges: [],
    date: "",
    timeSlot: "",
    concerns: ""
  });
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [inlineSuccess, setInlineSuccess] = useState(false);
  const [submittedBooking, setSubmittedBooking] = useState(null);


  // Sync configuration to LocalStorage
  const handleSaveConfig = (e) => {
    e.preventDefault();
    localStorage.setItem("scionfocus_cta_url", googleFormUrl);
    localStorage.setItem("scionfocus_use_google_form", useGoogleForm.toString());
    setIsConfigPanelOpen(false);
    toast.success("Settings updated successfully! CTA buttons are now configured.");
  };

  // Image error handling callback for beautiful resilience
  const handleImageError = (e, fallback) => {
    e.target.src = fallback;
  };

  // Primary Action handler for all CTA buttons
  const handleCtaAction = (programName = "", prefill = {}) => {
    // Reset bookingForm state on open to prevent cross-form leak, merging with optional prefill
    setBookingForm({
      childName: prefill.childName || "",
      childAge: prefill.childAge || "",
      parentName: prefill.parentName || "",
      phone: prefill.phone || "",
      challenges: prefill.challenges || [],
      date: prefill.date || "",
      timeSlot: prefill.timeSlot || "",
      concerns: prefill.concerns || ""
    });
    setBookingSuccess(false);

    if (programName) {
      setSelectedProgram(programName);
      // Pre-select challenge if matched
      let challengeKey = "";
      if (programName.toLowerCase().includes("reading") || programName.toLowerCase().includes("dyslexia")) {
        challengeKey = "Reading";
      } else if (programName.toLowerCase().includes("writing") || programName.toLowerCase().includes("dysgraphia")) {
        challengeKey = "Writing";
      } else if (programName.toLowerCase().includes("concentration") || programName.toLowerCase().includes("adhd")) {
        challengeKey = "Focus";
      }
      
      if (challengeKey) {
        setBookingForm(prev => ({
          ...prev,
          challenges: prefill.challenges && prefill.challenges.length > 0 ? prefill.challenges : [challengeKey]
        }));
      }
    } else {
      setSelectedProgram("");
    }

    if (useGoogleForm && googleFormUrl) {
      window.open(googleFormUrl, "_blank", "noopener,noreferrer");
    } else {
      setIsBookingOpen(true);
    }
  };

  // Quiz submission scorer & generator
  const evaluateQuiz = () => {
    let readingScore = quizAnswers.reading?.startsWith("Frequently") ? 3 : quizAnswers.reading?.startsWith("Sometimes") ? 2 : 1;
    let mathScore = quizAnswers.math?.startsWith("Frequently") ? 3 : quizAnswers.math?.startsWith("Sometimes") ? 2 : 1;
    let focusScore = quizAnswers.focus?.startsWith("Frequently") ? 3 : quizAnswers.focus?.startsWith("Sometimes") ? 2 : 1;
    let speechScore = quizAnswers.speech?.startsWith("Frequently") ? 3 : quizAnswers.speech?.startsWith("Sometimes") ? 2 : 1;

    let totalScore = readingScore + mathScore + focusScore + speechScore;
    let mainConcern = [];
    if (readingScore >= 2) mainConcern.push("Reading & Phonics (വായന ബുദ്ധിമുട്ട്)");
    if (mathScore >= 2) mainConcern.push("Math & Numbers (കണക്ക് ബുദ്ധിമുട്ട്)");
    if (focusScore >= 2) mainConcern.push("Concentration & Focus (ശ്രദ്ധക്കുറവ്)");
    if (speechScore >= 2) mainConcern.push("Speech & Communication (സംസാര വൈഷമ്യം)");

    let report = {
      level: totalScore > 8 ? "Moderate to High Support Required" : "Mild Support Required",
      advice: totalScore > 8 
        ? `Dear Parent, ${quizName || "your child"} shows indicators that can benefit significantly from structured multisensory remedial support. Early intervention is key to rebuilding confidence.` 
        : `Dear Parent, ${quizName || "your child"} has strong fundamental potential. A few tailored study habit modifications and minor remedial exercises will boost their confidence to 100%.`,
      recommendations: mainConcern.length > 0 ? mainConcern : ["General Study Skills & Confidence (പഠന രീതികൾ)"],
      isUrgent: totalScore > 8
    };

    setQuizResult(report);
    setQuizStateStep(5);
    toast.success("Diagnostic screening complete! View your personalized report below.");
  };

  const resetQuiz = () => {
    setQuizStateStep(0);
    setQuizName("");
    setQuizAge("");
    setQuizAnswers({ reading: "", math: "", focus: "", speech: "" });
    setQuizResult(null);
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    if (!bookingForm.childName || !bookingForm.childAge || !bookingForm.parentName || !bookingForm.date || !bookingForm.timeSlot) {
      toast.error("Please fill in all mandatory (*) fields.");
      return;
    }
    
    // Save locally to mock data persistence
    const savedBookings = JSON.parse(localStorage.getItem("scionfocus_bookings") || "[]");
    const submittedData = {
      ...bookingForm,
      id: Date.now(),
      submittedAt: new Date().toISOString()
    };
    savedBookings.push(submittedData);
    localStorage.setItem("scionfocus_bookings", JSON.stringify(savedBookings));

    setSubmittedBooking(submittedData);

    if (isBookingOpen) {
      setBookingSuccess(true);
    } else {
      setInlineSuccess(true);
    }
    toast.success("Booking submitted successfully! Our team will contact you shortly.");
  };


  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 font-dmsans selection:bg-amber-500/20 selection:text-amber-300">
      <Toaster position="top-center" richColors />

      {/* STICKY HEADER GLASSMORPHISM */}
      <header className="sticky top-0 z-50 bg-[#030712]/85 backdrop-blur-md border-b border-slate-800 smooth-transition" data-testid="header-container">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="#" className="flex items-center space-x-2 sm:space-x-3 group min-w-0 shrink sm:shrink-0" data-testid="nav-logo-link">
            {/* Logo 2 (Dark theme Sf logo) */}
            <div className="relative group-hover:scale-105 smooth-transition shrink-0">
              <svg width="44" height="44" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-sm">
                {/* Transparent dark square background */}
                <rect width="100" height="100" rx="20" fill="#0b1329" />
                
                {/* Yellow corner brackets */}
                <path d="M 18,32 L 18,18 L 32,18" stroke="#f59e0b" stroke-width="7" stroke-linecap="round" fill="none" />
                <path d="M 82,32 L 82,18 L 68,18" stroke="#f59e0b" stroke-width="7" stroke-linecap="round" fill="none" />
                <path d="M 18,68 L 18,82 L 32,82" stroke="#f59e0b" stroke-width="7" stroke-linecap="round" fill="none" />
                <path d="M 82,68 L 82,82 L 68,82" stroke="#f59e0b" stroke-width="7" stroke-linecap="round" fill="none" />
                
                {/* "Sf" text */}
                <text x="50" y="66" font-family="'Newsreader', 'Times New Roman', serif" font-size="52" font-weight="bold" fill="#ffffff" text-anchor="middle">Sf</text>
              </svg>
            </div>
            <div className="min-w-0 flex-1 sm:flex-initial">
              <span className="block font-outfit text-xs sm:text-2xl font-extrabold text-white tracking-tight leading-none truncate">
                SCIONFOCUS<span className="text-amber-500">Academy</span>
              </span>
              <span className="text-[9px] sm:text-xs font-medium text-amber-500/80 tracking-widest uppercase mt-1 block truncate">
                Remedial Support
              </span>
            </div>
          </a>

          {/* Nav Items - Desktop */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-8">
            <a href="#manifesto" className="text-sm font-semibold text-slate-300 hover:text-amber-400 smooth-transition whitespace-nowrap" data-testid="nav-link-manifesto">Manifesto</a>
            <a href="#assessment" className="text-sm font-semibold text-slate-300 hover:text-amber-400 smooth-transition whitespace-nowrap" data-testid="nav-link-quiz">Diagnostic Quiz</a>
            <a href="#programs" className="text-sm font-semibold text-slate-300 hover:text-amber-400 smooth-transition whitespace-nowrap" data-testid="nav-link-programs">Specialisms</a>
            <a href="#specialist" className="text-sm font-semibold text-slate-300 hover:text-amber-400 smooth-transition whitespace-nowrap" data-testid="nav-link-specialist">Rathi Mechery</a>
            <a href="#stories" className="text-sm font-semibold text-slate-300 hover:text-amber-400 smooth-transition whitespace-nowrap" data-testid="nav-link-stories">Parent Stories</a>
          </nav>

          {/* Header Actions */}
          <div className="flex items-center space-x-1.5 sm:space-x-4 shrink-0">
            <a 
              href="tel:+919400650509" 
              className="flex items-center text-slate-100 bg-slate-900 p-2 xl:px-4 xl:py-2 rounded-xl text-xs sm:text-sm font-bold border border-slate-800 hover:bg-slate-800 smooth-transition"
              data-testid="nav-phone-button"
              aria-label="Call Rathi Mechery"
            >
              <Phone className="h-4 w-4 xl:mr-2 text-amber-500" />
              <span className="hidden xl:inline">9400 65 05 09</span>
            </a>
            
            <button 
              onClick={() => handleCtaAction()}
              className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold text-xs sm:text-sm px-3 py-2 sm:px-5 sm:py-2.5 rounded-xl shadow-md active:scale-95 smooth-transition"
              data-testid="nav-booking-button"
            >
              Book Slot
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* HERO SECTION - ASYMMETRIC 12-COLUMN GRID */}
        <section className="relative overflow-hidden pt-12 pb-20 lg:pt-20 lg:pb-32" data-testid="hero-section">
          {/* Subtle natural backgrounds */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-950/40 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-red-950/30 rounded-full blur-3xl -z-10" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Storytelling & CTAs Column */}
              <div className="lg:col-span-7 space-y-8 text-left">
                <div className="inline-flex items-center space-x-2 bg-slate-900 border border-slate-800 px-3.5 py-1.5 rounded-full" data-testid="hero-badge">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  <span className="text-xs sm:text-sm font-bold text-slate-200 tracking-wide uppercase font-outfit">
                    Kerala Focus Bilingual Remedial Support
                  </span>
                </div>

                <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]" data-testid="hero-title">
                  Every Child Learns at a <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-500">
                    Beautiful, Unique Pace
                  </span>
                </h1>

                {/* Malayalam Quote */}
                <div className="relative border-l-4 border-red-800 pl-4 py-1" data-testid="hero-malayalam-quote">
                  <p className="font-newsreader text-lg sm:text-xl font-medium text-slate-100 leading-relaxed italic">
                    “3–15 വയസ്സുള്ള കുട്ടികളുടെ പഠന, വായന, എഴുത്ത്, ശ്രദ്ധ, സംസാര, പെരുമാറ്റ ബുദ്ധിമുട്ടുകൾ മനസ്സിലാക്കി, ശരിയായ മാർഗ്ഗനിർദ്ദേശവും വ്യക്തിഗത Remedial Support-ഉം നൽകി കുട്ടികളെ കൂടുതൽ ആത്മവിശ്വാസത്തോടെ പഠിക്കാൻ സഹായിക്കുന്നു.”
                  </p>
                </div>

                {/* English Subheading */}
                <p className="font-dmsans text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl" data-testid="hero-subtitle">
                  I help parents of children aged 3–15 overcome learning, reading, writing, concentration, speech and behavioural difficulties through personalized science-backed developmental coaching.
                </p>

                {/* High Converting Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <a 
                    href="#assessment"
                    className="inline-flex items-center justify-center bg-red-800 hover:bg-red-700 text-white font-bold text-base px-6 py-4 rounded-xl shadow-lg hover:shadow-xl active:scale-95 smooth-transition"
                    data-testid="hero-quiz-cta"
                  >
                    <Sparkles className="h-5 w-5 mr-2" />
                    Start AI Diagnostic Quiz (ഫ്രീ ടെസ്റ്റ്)
                  </a>
                  <button 
                    onClick={() => handleCtaAction("Hero Consultation")}
                    className="inline-flex items-center justify-center bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold text-base px-6 py-4 rounded-xl shadow-md hover:shadow-lg active:scale-95 smooth-transition"
                    data-testid="hero-booking-cta"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    Book 1-on-1 Session Slots
                  </button>
                </div>

                {/* Hero Stats */}
                <div className="grid grid-cols-3 gap-6 pt-10 border-t border-slate-800 max-w-lg">
                  <div data-testid="hero-stat-kids">
                    <span className="block font-outfit text-3xl sm:text-4xl font-extrabold text-amber-400 leading-none">500+</span>
                    <span className="text-xs sm:text-sm font-semibold text-slate-400 mt-2 block">Kerala Kids Helped</span>
                  </div>
                  <div data-testid="hero-stat-progress">
                    <span className="block font-outfit text-3xl sm:text-4xl font-extrabold text-red-500 leading-none">100%</span>
                    <span className="text-xs sm:text-sm font-semibold text-slate-400 mt-2 block">Empathetic Progress</span>
                  </div>
                  <div data-testid="hero-stat-age">
                    <span className="block font-outfit text-3xl sm:text-4xl font-extrabold text-amber-400 leading-none">3–15</span>
                    <span className="text-xs sm:text-sm font-semibold text-slate-400 mt-2 block">Target Age Group</span>
                  </div>
                </div>
              </div>

              {/* Specialist Child Frame Column */}
              <div className="lg:col-span-5 relative mt-8 lg:mt-0" data-testid="hero-image-col">
                <div className="relative mx-auto max-w-md lg:max-w-none">
                  {/* Decorative Frame border with golden studio glow */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-red-600 to-amber-500 rounded-3xl translate-x-3 translate-y-3 -z-10 opacity-35 blur-sm" />
                  
                  {/* Image container */}
                  <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border-4 border-slate-800 bg-slate-950">
                    <img 
                      src="https://tagmango.com/assets/photo-from-rathi-mechery-1-7055f65d7aceb2206010375da3953141.jpg"
                      onError={(e) => handleImageError(e, "https://images.unsplash.com/photo-1583468982228-19f19164aee2?crop=entropy&cs=srgb&fm=jpg&q=85")}
                      alt="Rathi Mechery Counseling Psychologist & Specialist" 
                      className="w-full h-full object-cover smooth-transition hover:scale-105"
                      data-testid="hero-image"
                    />
                  </div>

                  {/* Floating Trust Card 1 */}
                  <div className="absolute -left-6 bottom-12 bg-slate-900/95 backdrop-blur-md p-4 rounded-2xl shadow-lg border border-slate-800 flex items-center space-x-3 max-w-[220px]">
                    <div className="bg-amber-500/10 p-2 rounded-xl text-amber-400">
                      <Award className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white uppercase tracking-wider font-outfit">14+ Years Experience</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Empathetic Child Development Specialist</p>
                    </div>
                  </div>

                  {/* Floating Trust Card 2 */}
                  <div className="absolute -right-6 top-16 bg-slate-900/95 backdrop-blur-md p-4 rounded-2xl shadow-lg border border-slate-800 flex items-center space-x-3 max-w-[220px]">
                    <div className="bg-red-500/10 p-2 rounded-xl text-red-500">
                      <HeartHandshake className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white uppercase tracking-wider font-outfit">Multisensory Method</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Customized play-based coaching</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MARQUEE TICKER BANNER */}
        <section className="bg-[#020617] py-4.5 overflow-hidden border-y border-slate-800" data-testid="ticker-section">
          <div className="relative flex items-center w-full" data-testid="marquee-ticker-container">
            <div className="animate-marquee whitespace-nowrap flex items-center space-x-12">
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <Sparkles className="h-4.5 w-4.5 mr-2 text-amber-500 animate-pulse" />
                Empowering Neurodivergent Minds
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <CheckCircle2 className="h-4.5 w-4.5 mr-2 text-red-500" />
                സഹതാപത്തോടെ പഠന വൈകല്യ പരിഹാരം
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <Brain className="h-4.5 w-4.5 mr-2 text-amber-500" />
                Multisensory Learning Technique
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <HeartHandshake className="h-4.5 w-4.5 mr-2 text-red-500" />
                Zero Stigma, 100% Growth
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <Volume2 className="h-4.5 w-4.5 mr-2 text-amber-500" />
                Bilingual English & Malayalam Specialist
              </span>
              
              {/* Duplicate set for infinite loop */}
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <Sparkles className="h-4.5 w-4.5 mr-2 text-amber-500 animate-pulse" />
                Empowering Neurodivergent Minds
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <CheckCircle2 className="h-4.5 w-4.5 mr-2 text-red-500" />
                സഹതാപത്തോടെ പഠന വൈകല്യ പരിഹാരം
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <Brain className="h-4.5 w-4.5 mr-2 text-amber-500" />
                Multisensory Learning Technique
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <HeartHandshake className="h-4.5 w-4.5 mr-2 text-red-500" />
                Zero Stigma, 100% Growth
              </span>
              <span className="flex items-center text-slate-100 font-outfit text-sm font-bold tracking-wider uppercase">
                <Volume2 className="h-4.5 w-4.5 mr-2 text-amber-500" />
                Bilingual English & Malayalam Specialist
              </span>
            </div>
          </div>
        </section>

        {/* CORE PHILOSOPHY - BENTO MANIFESTO GRID */}
        <section id="manifesto" className="py-24 sm:py-32" data-testid="manifesto-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl text-left mb-16 space-y-4">
              <span className="caption_mono font-mono text-xs uppercase tracking-wider bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full">Our Core Philosophy</span>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight" data-testid="manifesto-heading">
                The Scion Focus Parent Manifesto
              </h2>
              <p className="font-newsreader text-lg sm:text-xl font-medium text-amber-400 leading-relaxed italic">
                വിശ്വാസത്തോടെയും സ്നേഹത്തോടെയും നിങ്ങളുടെ കുട്ടിയെ മികച്ച രീതിയിലേക്ക് മാറ്റിയെടുക്കാനുള്ള ഞങ്ങളുടെ 5 അചഞ്ചലമായ വാഗ്ദാനങ്ങൾ.
              </p>
            </div>

            {/* Bento Grid layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Manifesto 1 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 hover:border-amber-500/50 smooth-transition shadow-sm flex flex-col justify-between group" data-testid="manifesto-card-1">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div className="bg-amber-500/10 text-amber-400 p-3 rounded-2xl group-hover:bg-amber-500/20 smooth-transition">
                      <HeartHandshake className="h-6 w-6" />
                    </div>
                    <span className="manifesto-number text-4xl font-extrabold select-none opacity-30">01</span>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl font-extrabold text-white">Unconditional Empathy First</h3>
                    <p className="text-xs font-bold text-red-400 bg-red-500/10 px-2.5 py-0.5 rounded-md w-fit">കൂട്ടിച്ചേർത്തു നിർത്താം, മനസ്സിലാക്കാം</p>
                    <p className="text-sm font-medium text-slate-300 italic leading-relaxed">
                      കുട്ടികൾ അലസരോ പഠിക്കാൻ മടിയുള്ളവരോ അല്ല. അവർ ലോകത്തെ കാണുന്നതും ഗ്രഹിക്കുന്നതും മറ്റൊരു മനോഹരമായ രീതിയിലാണ്.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-400 mt-6 pt-4 border-t border-slate-800">
                  Your child is not lazy, slow, or intentionally avoiding study. Their brain simply processes the world through a different, beautiful lens.
                </p>
              </div>

              {/* Manifesto 2 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 hover:border-amber-500/50 smooth-transition shadow-sm flex flex-col justify-between group" data-testid="manifesto-card-2">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div className="bg-red-500/10 text-red-500 p-3 rounded-2xl group-hover:bg-red-500/20 smooth-transition">
                      <Brain className="h-6 w-6" />
                    </div>
                    <span className="manifesto-number text-4xl font-extrabold select-none opacity-30">02</span>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl font-extrabold text-white">Science-Backed Remediation</h3>
                    <p className="text-xs font-bold text-amber-300 bg-amber-500/10 px-2.5 py-0.5 rounded-md w-fit">ശാസ്ത്രീയമായ കളി-പഠന രീതികൾ</p>
                    <p className="text-sm font-medium text-slate-300 italic leading-relaxed">
                      വെറുതെ കാണാതെ പഠിപ്പിക്കുന്നതിലല്ല, മറിച്ച് പലതരം ഇന്ദ്രിയങ്ങളെ ഉണർത്തുന്ന ശാസ്ത്രീയമായ കളികളിലൂടെയാണ് ഞങ്ങൾ കുട്ടികളെ പഠിപ്പിക്കുന്നത്.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-400 mt-6 pt-4 border-t border-slate-800">
                  We don\'t believe in rote memorization. We use multisensory, gamified techniques that turn frustration into playful learning triumphs.
                </p>
              </div>

              {/* Manifesto 3 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 hover:border-amber-500/50 smooth-transition shadow-sm flex flex-col justify-between group" data-testid="manifesto-card-3">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div className="bg-amber-500/10 text-amber-400 p-3 rounded-2xl group-hover:bg-amber-500/20 smooth-transition">
                      <Award className="h-6 w-6" />
                    </div>
                    <span className="manifesto-number text-4xl font-extrabold select-none opacity-30">03</span>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl font-extrabold text-white">Confidence Over Comparison</h3>
                    <p className="text-xs font-bold text-red-400 bg-red-500/10 px-2.5 py-0.5 rounded-md w-fit">താരതമ്യങ്ങളില്ലാത്ത ആത്മവിശ്വാസം</p>
                    <p className="text-sm font-medium text-slate-300 italic leading-relaxed">
                      മറ്റൊരു കുട്ടിയുമായും ഞങ്ങൾ നിങ്ങളുടെ മകനെയോ മകളെയോ താരതമ്യം ചെയ്യില്ല. അവർ സ്വന്തം വേഗതയിൽ അഭിമാനത്തോടെ മുന്നേറട്ടെ.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-400 mt-6 pt-4 border-t border-slate-800">
                  No two children share the same timeline. We cultivate inner confidence so they learn to stand proud of their unique progress.
                </p>
              </div>

              {/* Manifesto 4 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 hover:border-amber-500/50 smooth-transition shadow-sm flex flex-col justify-between group md:col-span-2 lg:col-span-1" data-testid="manifesto-card-4">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div className="bg-red-500/10 text-red-500 p-3 rounded-2xl group-hover:bg-red-500/20 smooth-transition">
                      <User className="h-6 w-6" />
                    </div>
                    <span className="manifesto-number text-4xl font-extrabold select-none opacity-30">04</span>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl font-extrabold text-white">Kerala Parent Empowerment</h3>
                    <p className="text-xs font-bold text-amber-300 bg-amber-500/10 px-2.5 py-0.5 rounded-md w-fit">മാതാപിതാക്കൾക്ക് പൂർണ്ണ മാർഗ്ഗനിർദ്ദേശം</p>
                    <p className="text-sm font-medium text-slate-300 italic leading-relaxed">
                      ഈ യാത്രയിൽ നിങ്ങൾ തനിച്ചല്ല. കുട്ടിയെ വീട്ടിൽ എങ്ങനെ പിന്തുണയ്ക്കണമെന്ന് മാതാപിതാക്കൾക്ക് ഞങ്ങൾ ലളിതമായി പറഞ്ഞുതരുന്നു.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-400 mt-6 pt-4 border-t border-slate-800">
                  You are not alone in this journey. We provide continuous Malayalam & English counseling to help you navigate and support your child at home.
                </p>
              </div>

              {/* Manifesto 5 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 hover:border-amber-500/50 smooth-transition shadow-sm flex flex-col justify-between group md:col-span-2 lg:col-span-2" data-testid="manifesto-card-5">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div className="bg-amber-500/10 text-amber-400 p-3 rounded-2xl group-hover:bg-amber-500/20 smooth-transition">
                      <CheckCircle2 className="h-6 w-6" />
                    </div>
                    <span className="manifesto-number text-4xl font-extrabold select-none opacity-30">05</span>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl font-extrabold text-white">Lifelong Cognitive Transformation</h3>
                    <p className="text-xs font-bold text-red-400 bg-red-500/10 px-2.5 py-0.5 rounded-md w-fit">ജീവിതകാലം മുഴുവൻ പ്രയോജനപ്പെടുന്ന മാറ്റം</p>
                    <p className="text-sm font-medium text-slate-300 italic leading-relaxed">
                      ലക്ഷ്യം വെറും മാർക്ക് മാത്രമല്ല; മികച്ച ശ്രദ്ധ, വ്യക്തമായ സംസാരം, നല്ല എഴുത്ത്, മികച്ച ജീവിത നൈപുണ്യങ്ങൾ എന്നിവയാണ്.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-400 mt-6 pt-4 border-t border-slate-800">
                  Our target is not just next term\'s marks. It is long-term concentration, expressive speech, clear writing, and independent life skills.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* INSTANT AI SCREENING TOOL - INTERACTIVE QUIZ CARD */}
        <section id="assessment" className="py-24 sm:py-32 bg-[#020617]/40 border-y border-slate-800" data-testid="quiz-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center space-y-4 mb-12">
              <span className="caption_mono font-mono text-xs uppercase tracking-wider bg-red-500/10 text-red-400 px-3 py-1 rounded-full">Instant AI Screening Tool</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-extrabold text-white tracking-tight" data-testid="quiz-heading">
                AI Child Diagnostic & Remedial Advisor
              </h2>
              <p className="text-base text-slate-300 max-w-2xl mx-auto">
                കുട്ടിയുടെ വായന, എഴുത്ത്, ശ്രദ്ധ, സംസാരം എന്നിവയിലെ ബുദ്ധിമുട്ടുകൾ വിലയിരുത്തി ഒരു പ്രാഥമിക remedial റിപ്പോർട്ട് ഇപ്പോൾ തന്നെ നേടൂ.
              </p>
            </div>

            {/* QUIZ INTERACTIVE CONTAINER */}
            <div className="bg-slate-900/80 rounded-3xl border border-slate-800 shadow-2xl p-6 sm:p-10 relative overflow-hidden" data-testid="quiz-card">
              {/* Progress Bar */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-800">
                <div 
                  className="h-full bg-amber-500 smooth-transition" 
                  style={{ width: `${(quizStep / 5) * 100}%` }}
                />
              </div>

              {/* STEP 0: CHILD BASIC DETAILS */}
              {quizStep === 0 && (
                <div className="space-y-6 pt-4" data-testid="quiz-step-0">
                  <h3 className="font-outfit text-xl font-bold text-white">കുട്ടിയുടെ അടിസ്ഥാന വിവരങ്ങൾ നൽകുക (Child Details)</h3>
                  <p className="text-xs text-slate-400 italic">വിവരങ്ങൾ തികച്ചും രഹസ്യമായി സൂക്ഷിക്കുന്നതാണ്.</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2 text-left">
                      <label className="block text-sm font-bold text-slate-300">Child's First Name (കുട്ടിയുടെ പേര്) *</label>
                      <input 
                        type="text" 
                        value={quizName}
                        onChange={(e) => setQuizName(e.target.value)}
                        placeholder="Eg. Siddharth"
                        className="w-full px-4 py-3 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="quiz-input-name"
                      />
                    </div>

                    <div className="space-y-2 text-left">
                      <label className="block text-sm font-bold text-slate-300">Age (പ്രായം: 3 to 15 years) *</label>
                      <input 
                        type="number" 
                        value={quizAge}
                        onChange={(e) => setQuizAge(e.target.value)}
                        placeholder="Eg. 8"
                        min="3"
                        max="15"
                        className="w-full px-4 py-3 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="quiz-input-age"
                      />
                    </div>
                  </div>

                  <button 
                    disabled={!quizName || !quizAge}
                    onClick={() => setQuizStateStep(1)}
                    className="w-full sm:w-auto bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold text-base px-8 py-3.5 rounded-xl active:scale-95 disabled:opacity-50 disabled:pointer-events-none smooth-transition"
                    data-testid="quiz-start-button"
                  >
                    കുട്ടിയെ വിലയിരുത്തുക (Start Quiz)
                  </button>
                </div>
              )}

              {/* STEP 1: READING & SPELLING */}
              {quizStep === 1 && (
                <div className="space-y-6 pt-4 text-left" data-testid="quiz-step-1">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-widest font-outfit">Step 1 of 4: Reading & Spelling</span>
                    <span className="text-xs font-medium text-slate-400">Child: {quizName} (Age {quizAge})</span>
                  </div>
                  
                  <h3 className="font-outfit text-lg sm:text-xl font-bold text-white leading-snug">
                    Does your child struggle to blend letters, skip lines while reading, or reverse letters like b/d, p/q?
                  </h3>
                  <p className="text-sm font-medium text-amber-300 bg-amber-500/10 px-3 py-1 rounded-lg w-fit">
                    കുട്ടി വായിക്കുമ്പോൾ വരികൾ തെറ്റിക്കുകയോ അക്ഷരങ്ങൾ തിരിച്ചെഴുതുകയോ ചെയ്യുന്നുണ്ടോ?
                  </p>

                  <div className="space-y-3 pt-2">
                    {["Frequently (പലപ്പോഴും)", "Sometimes (ചിലപ്പോൾ)", "Rarely / Never (ഇല്ല/വളരെ കുറവ്)"].map((opt) => (
                      <label 
                        key={opt} 
                        className={`flex items-center space-x-3 p-4 rounded-xl border smooth-transition cursor-pointer ${
                          quizAnswers.reading === opt ? "border-amber-500 bg-amber-500/10" : "border-slate-800 hover:bg-slate-800/50"
                        }`}
                        data-testid="quiz-option-reading"
                      >
                        <input 
                          type="radio" 
                          name="reading" 
                          value={opt}
                          checked={quizAnswers.reading === opt}
                          onChange={(e) => setQuizAnswers(prev => ({ ...prev, reading: e.target.value }))}
                          className="h-4 w-4 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm sm:text-base text-slate-200 font-medium">{opt}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex space-x-4 pt-4">
                    <button 
                      onClick={() => setQuizStateStep(0)}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-6 py-3 rounded-xl smooth-transition"
                    >
                      Back
                    </button>
                    <button 
                      disabled={!quizAnswers.reading}
                      onClick={() => setQuizStateStep(2)}
                      className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold px-8 py-3 rounded-xl disabled:opacity-50 disabled:pointer-events-none smooth-transition"
                      data-testid="quiz-next-step-1"
                    >
                      Next Step
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: MATH & NUMBERS */}
              {quizStep === 2 && (
                <div className="space-y-6 pt-4 text-left" data-testid="quiz-step-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-widest font-outfit">Step 2 of 4: Math & Numbers</span>
                    <span className="text-xs font-medium text-slate-400">Child: {quizName}</span>
                  </div>
                  
                  <h3 className="font-outfit text-lg sm:text-xl font-bold text-white leading-snug">
                    Does your child face difficulties understanding numbers, basic math symbols, or telling time?
                  </h3>
                  <p className="text-sm font-medium text-amber-300 bg-amber-500/10 px-3 py-1 rounded-lg w-fit">
                    കുട്ടിക്ക് അക്കങ്ങൾ മനസ്സിലാക്കാനും കണക്കുകൂട്ടാനും സമയം നോക്കാനും ബുദ്ധിമുട്ടുണ്ടോ?
                  </p>

                  <div className="space-y-3 pt-2">
                    {["Frequently (പലപ്പോഴും)", "Sometimes (ചിലപ്പോൾ)", "Rarely / Never (ഇല്ല/വളരെ കുറവ്)"].map((opt) => (
                      <label 
                        key={opt} 
                        className={`flex items-center space-x-3 p-4 rounded-xl border smooth-transition cursor-pointer ${
                          quizAnswers.math === opt ? "border-amber-500 bg-amber-500/10" : "border-slate-800 hover:bg-slate-800/50"
                        }`}
                        data-testid="quiz-option-math"
                      >
                        <input 
                          type="radio" 
                          name="math" 
                          value={opt}
                          checked={quizAnswers.math === opt}
                          onChange={(e) => setQuizAnswers(prev => ({ ...prev, math: e.target.value }))}
                          className="h-4 w-4 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm sm:text-base text-slate-200 font-medium">{opt}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex space-x-4 pt-4">
                    <button 
                      onClick={() => setQuizStateStep(1)}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-6 py-3 rounded-xl smooth-transition"
                    >
                      Back
                    </button>
                    <button 
                      disabled={!quizAnswers.math}
                      onClick={() => setQuizStateStep(3)}
                      className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold px-8 py-3 rounded-xl disabled:opacity-50 disabled:pointer-events-none smooth-transition"
                      data-testid="quiz-next-step-2"
                    >
                      Next Step
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: CONCENTRATION & FOCUS */}
              {quizStep === 3 && (
                <div className="space-y-6 pt-4 text-left" data-testid="quiz-step-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-widest font-outfit">Step 3 of 4: Concentration & Focus</span>
                    <span className="text-xs font-medium text-slate-400">Child: {quizName}</span>
                  </div>
                  
                  <h3 className="font-outfit text-lg sm:text-xl font-bold text-white leading-snug">
                    Is it hard for your child to sit still in one place, follow multiple instructions, or complete a single task?
                  </h3>
                  <p className="text-sm font-medium text-amber-300 bg-amber-500/10 px-3 py-1 rounded-lg w-fit">
                    ഒരു സ്ഥലത്ത് അടങ്ങിയിരിക്കാനോ നിർദ്ദേശങ്ങൾ ശ്രദ്ധിക്കാനോ കുട്ടി ബുദ്ധിമുട്ടുന്നുണ്ടോ?
                  </p>

                  <div className="space-y-3 pt-2">
                    {["Frequently (പലപ്പോഴും)", "Sometimes (ചിലപ്പോൾ)", "Rarely / Never (ഇല്ല/വളരെ കുറവ്)"].map((opt) => (
                      <label 
                        key={opt} 
                        className={`flex items-center space-x-3 p-4 rounded-xl border smooth-transition cursor-pointer ${
                          quizAnswers.focus === opt ? "border-amber-500 bg-amber-500/10" : "border-slate-800 hover:bg-slate-800/50"
                        }`}
                        data-testid="quiz-option-focus"
                      >
                        <input 
                          type="radio" 
                          name="focus" 
                          value={opt}
                          checked={quizAnswers.focus === opt}
                          onChange={(e) => setQuizAnswers(prev => ({ ...prev, focus: e.target.value }))}
                          className="h-4 w-4 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm sm:text-base text-slate-200 font-medium">{opt}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex space-x-4 pt-4">
                    <button 
                      onClick={() => setQuizStateStep(2)}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-6 py-3 rounded-xl smooth-transition"
                    >
                      Back
                    </button>
                    <button 
                      disabled={!quizAnswers.focus}
                      onClick={() => setQuizStateStep(4)}
                      className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold px-8 py-3 rounded-xl disabled:opacity-50 disabled:pointer-events-none smooth-transition"
                      data-testid="quiz-next-step-3"
                    >
                      Next Step
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: SPEECH & EXPRESSION */}
              {quizStep === 4 && (
                <div className="space-y-6 pt-4 text-left" data-testid="quiz-step-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-widest font-outfit">Step 4 of 4: Speech & Expression</span>
                    <span className="text-xs font-medium text-slate-400">Child: {quizName}</span>
                  </div>
                  
                  <h3 className="font-outfit text-lg sm:text-xl font-bold text-white leading-snug">
                    Does your child find it difficult to express their thoughts clearly in words or show shyness/anxiety in speaking?
                  </h3>
                  <p className="text-sm font-medium text-amber-300 bg-amber-500/10 px-3 py-1 rounded-lg w-fit">
                    ആശയങ്ങൾ വ്യക്തമായി സംസാരിച്ചു ഫലിപ്പിക്കാൻ ബുദ്ധിമുട്ടോ ആളുകളോട് സംസാരിക്കാൻ മടിയോ ഉണ്ടോ?
                  </p>

                  <div className="space-y-3 pt-2">
                    {["Frequently (പലപ്പോഴും)", "Sometimes (ചിലപ്പോൾ)", "Rarely / Never (ഇല്ല/വളരെ കുറവ്)"].map((opt) => (
                      <label 
                        key={opt} 
                        className={`flex items-center space-x-3 p-4 rounded-xl border smooth-transition cursor-pointer ${
                          quizAnswers.speech === opt ? "border-amber-500 bg-amber-500/10" : "border-slate-800 hover:bg-slate-800/50"
                        }`}
                        data-testid="quiz-option-speech"
                      >
                        <input 
                          type="radio" 
                          name="speech" 
                          value={opt}
                          checked={quizAnswers.speech === opt}
                          onChange={(e) => setQuizAnswers(prev => ({ ...prev, speech: e.target.value }))}
                          className="h-4 w-4 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm sm:text-base text-slate-200 font-medium">{opt}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex space-x-4 pt-4">
                    <button 
                      onClick={() => setQuizStateStep(3)}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-6 py-3 rounded-xl smooth-transition"
                    >
                      Back
                    </button>
                    <button 
                      disabled={!quizAnswers.speech}
                      onClick={evaluateQuiz}
                      className="bg-red-800 hover:bg-red-700 text-white font-bold px-8 py-3 rounded-xl disabled:opacity-50 disabled:pointer-events-none smooth-transition"
                      data-testid="quiz-submit-answers-button"
                    >
                      Calculate Report
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 5: PERSONALIZED DIAGNOSTIC REPORT CARD */}
              {quizStep === 5 && quizResult && (
                <div className="space-y-6 pt-4 text-left" data-testid="quiz-result-card">
                  <div className="flex items-center space-x-2 text-amber-400">
                    <CheckCircle2 className="h-6 w-6 text-amber-500" />
                    <span className="font-outfit text-xs uppercase tracking-widest font-extrabold bg-amber-500/10 px-3 py-1 rounded-full">Assessment Complete</span>
                  </div>

                  <div className="border border-slate-800 rounded-2xl bg-slate-950/60 p-6 space-y-4">
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center border-b border-slate-800 pb-3">
                      <div>
                        <h4 className="font-outfit font-extrabold text-white text-lg">Preliminary Analysis: {quizName}</h4>
                        <p className="text-sm text-slate-400 mt-1">Age Group: {quizAge} Years</p>
                      </div>
                      <span className={`mt-2 sm:mt-0 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                        quizResult.isUrgent ? "bg-red-950 text-red-400 border border-red-900/30" : "bg-amber-500/15 text-amber-400"
                      }`}>
                        {quizResult.level}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm sm:text-base font-semibold text-slate-200 leading-relaxed font-newsreader italic">
                        "{quizResult.advice}"
                      </p>
                    </div>

                    <div className="space-y-3 pt-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Suggested Action Plan & Programs:</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {quizResult.recommendations.map((rec) => (
                          <li key={rec} className="flex items-center text-sm font-semibold text-slate-300">
                            <Sparkles className="h-4.5 w-4.5 text-amber-400 mr-2 shrink-0 animate-pulse" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <button 
                      onClick={() => {
                        handleCtaAction("Diagnostic Quiz Result Option", {
                          childName: quizName,
                          childAge: quizAge,
                          concerns: `Automatically generated via Quiz Report:\nAssessed Level: ${quizResult.level}\nRecommended paths: ${quizResult.recommendations.join(", ")}`
                        });
                      }}
                      className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold px-8 py-3.5 rounded-xl text-center active:scale-95 smooth-transition"
                      data-testid="quiz-book-remedial-button"
                    >
                      Schedule Free Expert Consultation
                    </button>
                    <button 
                      onClick={resetQuiz}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-6 py-3.5 rounded-xl smooth-transition"
                    >
                      Retake Quiz
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* SPECIALIZED REMEDIAL PROGRAMS */}
        <section id="programs" className="py-24 sm:py-32 bg-[#030712]" data-testid="programs-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl text-left mb-16 space-y-4">
              <span className="caption_mono font-mono text-xs uppercase tracking-wider bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full">Expert Programs</span>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight" data-testid="programs-heading">
                Specialized Remedial Coaching Programs
              </h2>
              <p className="font-newsreader text-lg sm:text-xl font-medium text-amber-400 leading-relaxed italic">
                നിങ്ങളുടെ കുട്ടിയുടെ വികാസത്തിനായി Scion Focus Academy രൂപകൽപ്പന ചെയ്ത മികച്ച കോഴ്സുകളും വർക്ക്ഷോപ്പുകളും.
              </p>
            </div>

            {/* 3 Column Program Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Program 1 */}
              <div className="bg-slate-900/60 rounded-3xl border border-slate-800 p-8 shadow-sm flex flex-col justify-between group hover:shadow-md smooth-transition" data-testid="program-card-1">
                <div className="space-y-6">
                  <div className="bg-amber-500/10 text-amber-400 p-4 rounded-2xl w-fit group-hover:bg-amber-500/20 smooth-transition">
                    <BookOpen className="h-7 w-7" />
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl sm:text-2xl font-bold text-white leading-tight">
                      Dyslexia & Reading Remedial Support
                    </h3>
                    <p className="text-sm font-semibold text-red-400 bg-red-500/10 px-3 py-1 rounded-lg w-fit">
                      വായന ബുദ്ധിമുട്ടുകൾ പരിഹാരം
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed pt-2">
                      Focused phonology, blending, letter-reversal modification, visual tracking, reading flow, and reading comprehension support.
                    </p>
                  </div>

                  <ul className="space-y-2.5 pt-2 text-left">
                    {["45-minute customized sessions", "Multisensory learning methods", "Interactive phonics games & worksheets", "Bi-weekly parent progress reviews"].map((feat) => (
                      <li key={feat} className="flex items-start text-sm text-slate-300">
                        <CheckCircle2 className="h-5 w-5 text-amber-500 mr-2 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button 
                  onClick={() => handleCtaAction("Dyslexia Reading Remedial Support")}
                  className="w-full mt-8 bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-3 px-5 rounded-xl hover:shadow active:scale-95 smooth-transition"
                  data-testid="program-enquire-button-1"
                >
                  Enquire About Program
                </button>
              </div>

              {/* Program 2 */}
              <div className="bg-slate-900/60 rounded-3xl border border-slate-800 p-8 shadow-sm flex flex-col justify-between group hover:shadow-md smooth-transition" data-testid="program-card-2">
                <div className="space-y-6">
                  <div className="bg-red-500/10 text-red-400 p-4 rounded-2xl w-fit group-hover:bg-red-500/20 smooth-transition">
                    <FileText className="h-7 w-7" />
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl sm:text-2xl font-bold text-white leading-tight">
                      Dysgraphia & Writing Confidence
                    </h3>
                    <p className="text-sm font-semibold text-red-400 bg-red-500/10 px-3 py-1 rounded-lg w-fit">
                      എഴുത്ത് വൈകല്യങ്ങൾ പരിഹാരം
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed pt-2">
                      Assisting children with letter tracing, grip improvement, horizontal/vertical spacing, spelling memory, and sentence assembly.
                    </p>
                  </div>

                  <ul className="space-y-2.5 pt-2 text-left">
                    {["Fine-motor skills activation", "Spatial layout & spacing guides", "Structured letter formation techniques", "Confidence-building writing exercises"].map((feat) => (
                      <li key={feat} className="flex items-start text-sm text-slate-300">
                        <CheckCircle2 className="h-5 w-5 text-amber-500 mr-2 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button 
                  onClick={() => handleCtaAction("Dysgraphia Writing Confidence")}
                  className="w-full mt-8 bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-3 px-5 rounded-xl hover:shadow active:scale-95 smooth-transition"
                  data-testid="program-enquire-button-2"
                >
                  Enquire About Program
                </button>
              </div>

              {/* Program 3 */}
              <div className="bg-slate-900/60 rounded-3xl border border-slate-800 p-8 shadow-sm flex flex-col justify-between group hover:shadow-md smooth-transition" data-testid="program-card-3">
                <div className="space-y-6">
                  <div className="bg-amber-500/10 text-amber-400 p-4 rounded-2xl w-fit group-hover:bg-amber-500/20 smooth-transition">
                    <Brain className="h-7 w-7" />
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-outfit text-xl sm:text-2xl font-bold text-white leading-tight">
                      ADHD Concentration Exercises
                    </h3>
                    <p className="text-sm font-semibold text-red-400 bg-red-500/10 px-3 py-1 rounded-lg w-fit">
                      ശ്രദ്ധക്കുറവ് പരിഹാര വ്യായാമങ്ങൾ
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed pt-2">
                      Cognitive attention-enhancement routines, sitting tolerance modification, and executive planning habits for hyperactive children.
                    </p>
                  </div>

                  <ul className="space-y-2.5 pt-2 text-left">
                    {["Sitting tolerance conditioning", "Gamified cognitive brain tasks", "Focus-trigger home schedule plans", "Parent counselor guiding sessions"].map((feat) => (
                      <li key={feat} className="flex items-start text-sm text-slate-300">
                        <CheckCircle2 className="h-5 w-5 text-amber-500 mr-2 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button 
                  onClick={() => handleCtaAction("ADHD Concentration Exercises")}
                  className="w-full mt-8 bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-3 px-5 rounded-xl hover:shadow active:scale-95 smooth-transition"
                  data-testid="program-enquire-button-3"
                >
                  Enquire About Program
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* SCHEDULE 1-ON-1 CONSULTATION */}
        <section id="booking" className="py-24 sm:py-32 bg-[#020617]/40 border-t border-slate-800" data-testid="booking-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 sm:gap-16 items-start">
              {/* Context Columns */}
              <div className="lg:col-span-5 space-y-6 text-left">
                <span className="caption_mono font-mono text-xs uppercase tracking-wider bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full">Expert Consultation</span>
                <h2 className="font-outfit text-3xl sm:text-4xl font-extrabold text-white leading-snug">
                  Schedule an Empathetic 1-on-1 Consultation
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  കുട്ടിയുടെ വൈഷമ്യങ്ങളെപ്പറ്റി സംസാരിക്കാനും പരിഹാരങ്ങൾ കണ്ടെത്താനും ഒരു സ്പെഷ്യലിസ്റ്റുമായി അപ്പോയിന്റ്മെന്റ് ബുക്ക് ചെയ്യുക.
                </p>

                {/* Benefits List */}
                <div className="space-y-6 pt-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-amber-500/10 text-amber-400 p-1.5 rounded-lg mt-0.5">
                      <HeartHandshake className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-outfit text-base font-bold text-white">പൂർണ്ണമായ കരുതലും പരിഗണനയും</h4>
                      <p className="text-xs sm:text-sm text-slate-400 mt-1">ഒരു കുട്ടിയുടെയും കുറവുകളെ വിമർശിക്കാതെ സ്നേഹത്തോടെ മനസ്സിലാക്കുന്നു.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-amber-500/10 text-amber-400 p-1.5 rounded-lg mt-0.5">
                      <Clock className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-outfit text-base font-bold text-white">45-മിനിറ്റ് ഡയഗ്നോസ്റ്റിക് സെഷൻ</h4>
                      <p className="text-xs sm:text-sm text-slate-400 mt-1">കുട്ടിയുടെ വ്യക്തിഗത പ്രശ്നങ്ങൾ കൃത്യമായി മനസ്സിലാക്കുന്നു.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-amber-500/10 text-amber-400 p-1.5 rounded-lg mt-0.5">
                      <Volume2 className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-outfit text-base font-bold text-white">ബഹുഭാഷാ പിന്തുണ (Bilingual)</h4>
                      <p className="text-xs sm:text-sm text-slate-400 mt-1">മലയാളത്തിലും ഇംഗ്ലീഷിലും കാര്യങ്ങൾ കൃത്യമായി പറഞ്ഞുതരുന്നു.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Consultation Booking Form Card */}
              <div className="lg:col-span-7 bg-slate-900/80 rounded-3xl border border-slate-800 shadow-2xl p-6 sm:p-10 text-left">
                {inlineSuccess ? (
                  <div className="py-8 text-center space-y-5" data-testid="inline-booking-success-message">
                    <div className="h-16 w-16 bg-amber-500/10 text-amber-400 rounded-full flex items-center justify-center mx-auto shadow">
                      <CheckCircle2 className="h-10 w-10 text-amber-500" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-outfit text-2xl font-bold text-white">Appointment Slot Confirmed!</h3>
                      <p className="text-sm font-medium text-amber-400">കുട്ടിയുടെ ഫ്രീ കൗൺസിലിങ് ഉറപ്പാക്കിയിട്ടുണ്ട്.</p>
                      <p className="text-sm text-slate-300 max-w-md mx-auto pt-2">
                        Thank you {submittedBooking?.parentName || bookingForm.parentName}. We have saved your 45-minute diagnostic slot on <strong>{submittedBooking?.date || bookingForm.date}</strong> at <strong>{submittedBooking?.timeSlot || bookingForm.timeSlot}</strong> for <strong>{submittedBooking?.childName || bookingForm.childName}</strong>.
                      </p>
                      <p className="text-xs text-slate-400 pt-1">
                        Specialist Rathi Mechery will call you on <strong>{submittedBooking?.phone || bookingForm.phone}</strong>.
                      </p>
                    </div>
                    <div className="pt-4">
                      <button 
                        onClick={() => {
                          setInlineSuccess(false);
                          setBookingForm({
                            childName: "",
                            childAge: "",
                            parentName: "",
                            phone: "",
                            challenges: [],
                            date: "",
                            timeSlot: "",
                            concerns: ""
                          });
                        }}
                        className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold px-8 py-3 rounded-xl smooth-transition"
                      >
                        Book Another Session
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleBookingSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-slate-300">Child's Name (കുട്ടിയുടെ പേര്) *</label>
                        <input 
                          type="text" 
                          required
                          placeholder="Eg. Rohan"
                          value={bookingForm.childName}
                          onChange={(e) => setBookingForm(prev => ({ ...prev, childName: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm smooth-transition"
                          data-testid="booking-child-name"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-slate-300">Child's Age (കുട്ടിയുടെ പ്രായം) *</label>
                        <input 
                          type="number" 
                          required
                          placeholder="Eg. 7"
                          value={bookingForm.childAge}
                          onChange={(e) => setBookingForm(prev => ({ ...prev, childAge: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm smooth-transition"
                          data-testid="booking-child-age"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-slate-300">Parent's Name (രക്ഷിതാവിന്റെ പേര്) *</label>
                        <input 
                          type="text" 
                          required
                          placeholder="Eg. Rajesh Kumar"
                          value={bookingForm.parentName}
                          onChange={(e) => setBookingForm(prev => ({ ...prev, parentName: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm smooth-transition"
                          data-testid="booking-parent-name"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-slate-300">Contact Number (ഫോൺ നമ്പർ) *</label>
                        <input 
                          type="tel" 
                          required
                          placeholder="Eg. 9400650509"
                          value={bookingForm.phone}
                          onChange={(e) => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm smooth-transition"
                          data-testid="booking-contact-number"
                        />
                      </div>
                    </div>

                    {/* Multi-select Challenges checkboxes */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-300 block">Select Challenges (കുട്ടിയുടെ ബുദ്ധിമുട്ടുകൾ തിരഞ്ഞെടുക്കുക)</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {["Reading", "Writing", "Focus", "Memory", "Speech", "Behavior"].map((chal) => {
                          const isChecked = bookingForm.challenges.includes(chal);
                          return (
                            <button
                              key={chal}
                              type="button"
                              onClick={() => {
                                      setBookingForm(prev => ({
                                        ...prev,
                                        challenges: isChecked 
                                          ? prev.challenges.filter(c => c !== chal)
                                          : [...prev.challenges, chal]
                                      }));
                              }}
                              className={`px-3 py-2 border rounded-xl text-xs sm:text-sm font-semibold smooth-transition text-left flex items-center justify-between ${
                                isChecked 
                                  ? "border-amber-500 bg-amber-500/10 text-amber-300" 
                                  : "border-slate-800 hover:bg-slate-800 text-slate-300"
                              }`}
                              data-testid={`booking-challenge-${chal.toLowerCase()}`}
                            >
                              <span>{chal}</span>
                              {isChecked && <CheckCircle2 className="h-4 w-4 text-amber-500 shrink-0 ml-1" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-slate-300">Choose Consultation Date *</label>
                        <input 
                          type="date" 
                          required
                          value={bookingForm.date}
                          onChange={(e) => setBookingForm(prev => ({ ...prev, date: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm smooth-transition"
                          data-testid="booking-date"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-slate-300">Choose Consultation Time Slot *</label>
                        <select 
                          required
                          value={bookingForm.timeSlot}
                          onChange={(e) => setBookingForm(prev => ({ ...prev, timeSlot: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm smooth-transition"
                          data-testid="booking-time-slot"
                        >
                          <option value="">Select a time slot</option>
                          <option value="10:00 AM - 10:45 AM">10:00 AM - 10:45 AM (Morning)</option>
                          <option value="11:30 AM - 12:15 PM">11:30 AM - 12:15 PM (Morning)</option>
                          <option value="03:00 PM - 03:45 PM">03:00 PM - 03:45 PM (Afternoon)</option>
                          <option value="05:30 PM - 06:15 PM">05:30 PM - 06:15 PM (Evening)</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-slate-300">Describe specific concerns (മറ്റ് വിവരങ്ങൾ)</label>
                      <textarea 
                        rows="3"
                        placeholder="Share what issues Rohan faces..."
                        value={bookingForm.concerns}
                        onChange={(e) => setBookingForm(prev => ({ ...prev, concerns: e.target.value }))}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm smooth-transition resize-none"
                        data-testid="booking-description"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <button 
                        type="submit"
                        className="flex-1 bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-3.5 px-6 rounded-xl shadow active:scale-95 text-center text-sm sm:text-base smooth-transition"
                        data-testid="booking-submit-button"
                      >
                        Confirm Booking (ഫ്രീ കൗൺസിലിങ് ഉറപ്പാക്കുക)
                      </button>
                      {useGoogleForm && googleFormUrl && (
                        <a 
                          href={googleFormUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="border border-slate-800 hover:bg-slate-800 text-slate-300 font-bold py-3.5 px-5 rounded-xl text-center text-xs sm:text-sm flex items-center justify-center smooth-transition"
                          data-testid="booking-google-form-button"
                        >
                          <Settings className="h-4 w-4 mr-1.5" />
                          Go to Google Form Link
                        </a>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* MEET THE REMEDIAL SPECIALIST */}
        <section id="specialist" className="py-24 sm:py-32" data-testid="specialist-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              
              {/* Specialist Portrait Frame */}
              <div className="lg:col-span-5 relative order-last lg:order-first" data-testid="specialist-image-col">
                <div className="relative mx-auto max-w-sm lg:max-w-none">
                  {/* Decorative Frame with golden-amber studio backdrop glow */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-amber-500 to-red-600 rounded-3xl translate-x-3 translate-y-3 -z-10 opacity-35 blur-sm" />
                  
                  {/* Image wrapper */}
                  <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border-4 border-slate-800 bg-slate-950">
                    <img 
                      src="https://tagmango.com/assets/img-20260915-wa0002-97c2c54d7848c499e569e2811e99b4e8.jpg"
                      onError={(e) => handleImageError(e, "/brand-photo/portrait.jpg")}
                      alt="Counseling Psychologist Rathi Mechery" 
                      className="w-full h-full object-cover smooth-transition hover:scale-105"
                      data-testid="specialist-image"
                    />
                  </div>

                  {/* Caption badge overlay */}
                  <div className="absolute inset-x-0 bottom-6 px-6 text-center">
                    <div className="bg-slate-900/95 backdrop-blur-md px-5 py-3 rounded-2xl border border-slate-800 shadow-lg mx-auto w-fit">
                      <p className="text-sm font-bold text-white font-outfit">Rathi Mechery</p>
                      <p className="text-xs text-amber-400 font-medium">Founder & Counseling Psychologist</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio details */}
              <div className="lg:col-span-7 space-y-6 text-left" data-testid="specialist-info-col">
                <span className="caption_mono font-mono text-xs uppercase tracking-wider bg-red-500/10 text-red-400 px-3 py-1 rounded-full">Meet the Specialist</span>
                <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight" data-testid="specialist-title">
                  രതി മേച്ചേരി | Rathi Mechery
                </h2>
                <p className="text-base sm:text-lg font-bold text-amber-400 font-outfit">
                  Counseling Psychologist & Remedial Specialist (14+ Years Experience)
                </p>

                {/* Structured Credentials */}
                <div className="space-y-4 pt-2" data-testid="specialist-bio-text">
                  <div className="flex items-start space-x-3">
                    <div className="bg-amber-500/10 text-amber-400 p-1 rounded-lg shrink-0 mt-0.5">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm sm:text-base font-semibold text-slate-100">
                        14 വർഷത്തോളം ഈ ഫീൽഡിൽ പരിചയസമ്പത്തുള്ള കൗൺസിലിംഗ് സൈക്കോളജിസ്റ്റ് രതി മേച്ചേരി.
                      </p>
                      <p className="text-sm text-slate-400">
                        Over 14 years of professional experience as an empathetic Counseling Psychologist and child specialist.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-amber-500/10 text-amber-400 p-1 rounded-lg shrink-0 mt-0.5">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm sm:text-base font-semibold text-slate-100">
                        2019-2021 കാലഘട്ടങ്ങളിൽ തൃശ്ശൂരിലെ ചൈൽഡ് വെൽഫെയർ കമ്മറ്റി മെമ്പർ ആയിരുന്നു.
                      </p>
                      <p className="text-sm text-slate-400">
                        Distinguished former Child Welfare Committee (CWC) Member in Thrissur from 2019 to 2021.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-amber-500/10 text-amber-400 p-1 rounded-lg shrink-0 mt-0.5">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm sm:text-base font-semibold text-slate-100">
                        ഇപ്പോൾ 5 വർഷത്തോളമായി തൃശ്ശൂർ കളക്ടറേറ്റിൽ പ്രവർത്തിക്കുന്ന ORC (Our Responsibility to Children) എന്ന സർക്കാർ പ്രോജക്റ്റിന്റെ District Resource Centre ൽ Remedial Trainer ആണ്.
                      </p>
                      <p className="text-sm text-slate-400">
                        Currently serving for over 5 years as the Remedial Trainer at the District Resource Centre for the ORC (Our Responsibility to Children) Government Project under the Thrissur Collectorate.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Specialist Action Row */}
                <div className="flex flex-wrap gap-4 pt-4">
                  <button 
                    onClick={() => handleCtaAction("Specialist Direct Slot Booking")}
                    className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-3 px-6 rounded-xl text-sm sm:text-base shadow active:scale-95 smooth-transition"
                    data-testid="specialist-cta-button"
                  >
                    Schedule Consultation slots
                  </button>
                  <a 
                    href="https://wa.me/919810765443?text=Hello%20Rathi%20Mechery%2C%20I%20would%20like%20to%20enquire%20about%20remedial%20support%20for%20my%20child."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-slate-900 hover:bg-slate-800 text-white font-bold border border-slate-800 py-3 px-5 rounded-xl text-xs sm:text-sm flex items-center smooth-transition"
                    data-testid="specialist-whatsapp-link"
                  >
                    <MessageSquare className="h-4.5 w-4.5 mr-1.5 text-amber-400" />
                    Chat via WhatsApp
                  </a>
                  <a 
                    href="tel:+919400650509"
                    className="bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 py-3 px-5 rounded-xl text-xs sm:text-sm flex items-center smooth-transition"
                    data-testid="specialist-phone-link"
                  >
                    <Phone className="h-4.5 w-4.5 mr-1.5 text-slate-400" />
                    Call: 9400 65 05 09
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PARENT STORIES - TESTIMONIALS */}
        <section id="stories" className="py-24 sm:py-32 bg-slate-950/20 border-y border-slate-800" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl text-left mb-16 space-y-4">
              <span className="caption_mono font-mono text-xs uppercase tracking-wider bg-red-500/10 text-red-400 px-3 py-1 rounded-full">Success Stories</span>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight" data-testid="testimonials-heading">
                Transformations from Kerala Parents
              </h2>
              <p className="font-newsreader text-lg sm:text-xl font-medium text-amber-400 leading-relaxed italic">
                തങ്ങളുടെ മക്കളുടെ മാറ്റം നേരിട്ടു കണ്ട് ആശ്വസിച്ച മാതാപിതാക്കളുടെ വാക്കുകൾ.
              </p>
            </div>

            {/* Testimonials 3 Column Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Testimonial 1 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 shadow-sm flex flex-col justify-between" data-testid="testimonial-card-1">
                <div className="space-y-6 text-left">
                  {/* Stars */}
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Sparkles key={s} className="h-4.5 w-4.5 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  
                  {/* Malayalam & English bilingual block */}
                  <div className="space-y-4">
                    <p className="font-newsreader text-base font-semibold text-slate-100 leading-relaxed italic">
                      “എന്റെ മോൻ Sidharth വായനയിൽ വളരെ പിന്നിലായിരുന്നു. അക്ഷരങ്ങൾ കൂട്ടി വായിക്കാൻ അവന് വലിയ ബുദ്ധിമുട്ടായിരുന്നു. Scion Academy-യിലെ remedial ക്ലാസുകൾ കളിയിലൂടെയാണ് അവനെ അക്ഷരങ്ങൾ പഠിപ്പിച്ചത്. ഇപ്പോൾ അവൻ സ്വയം കഥാപുസ്തകങ്ങൾ എടുത്തു വായിക്കുന്നു!”
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed pt-2 border-t border-slate-800">
                      “My son struggled heavily with blending letters. The remedial support focused on play-based learning. Now he happily picks up storybooks and reads independently!”
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3.5 pt-6 mt-6 border-t border-slate-800">
                  <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 font-extrabold font-outfit">
                    BR
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-bold text-white">Bindu Rajesh</h4>
                    <p className="text-xs text-slate-400 font-medium">Mother of Sidharth • Ernakulam</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 shadow-sm flex flex-col justify-between" data-testid="testimonial-card-2">
                <div className="space-y-6 text-left">
                  {/* Stars */}
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Sparkles key={s} className="h-4.5 w-4.5 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  
                  <div className="space-y-4">
                    <p className="font-newsreader text-base font-semibold text-slate-100 leading-relaxed italic">
                      “Ryan-ന് പഠിച്ച കാര്യങ്ങൾ പെട്ടെന്ന് മറന്നുപോകുന്ന സ്വഭാവമുണ്ടായിരുന്നു. കൂടാതെ concentration വളരെ കുറവായിരുന്നു. ഇവിടുത്തെ cognitive വ്യായാമങ്ങൾ വഴി അവന്റെ ശ്രദ്ധയും ഓർമ്മശക്തിയും വലിയ തോതിൽ മെച്ചപ്പെട്ടു.”
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed pt-2 border-t border-slate-800">
                      “Ryan used to forget what he studied within minutes. The customized attention exercises and brain-training activities here significantly enhanced his memory and focus.”
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3.5 pt-6 mt-6 border-t border-slate-800">
                  <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 font-extrabold font-outfit">
                    AH
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-bold text-white">Anwar H.</h4>
                    <p className="text-xs text-slate-400 font-medium">Father of Ryan • Kozhikode</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 shadow-sm flex flex-col justify-between" data-testid="testimonial-card-3">
                <div className="space-y-6 text-left">
                  {/* Stars */}
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Sparkles key={s} className="h-4.5 w-4.5 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  
                  <div className="space-y-4">
                    <p className="font-newsreader text-base font-semibold text-slate-100 leading-relaxed italic">
                      “Meenakshi-ക്ക് സംസാരിക്കുമ്പോൾ വാക്കുകൾ കിട്ടാൻ ബുദ്ധിമുട്ടായിരുന്നു. ആളുകളുടെ മുന്നിൽ വരാൻ അവൾക്ക് പേടിയായിരുന്നു. ഇവിടുത്തെ ടീച്ചർമാരുടെ സ്നേഹത്തോടെയുള്ള സംസാര പരിശീലനം കൊണ്ട് അവളുടെ പേടി മാറി, അവൾ ഇപ്പോൾ നന്നായി സംസാരിക്കുന്നുണ്ട്.”
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed pt-2 border-t border-slate-800">
                      “Meenakshi had speech struggles and was extremely shy. The warm, therapeutic support of the teachers here cured her anxiety, and she now communicates so fluently.”
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3.5 pt-6 mt-6 border-t border-slate-800">
                  <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 font-extrabold font-outfit">
                    SN
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-bold text-white">Saritha Nair</h4>
                    <p className="text-xs text-slate-400 font-medium">Mother of Meenakshi • Trivandrum</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-200 pt-20 pb-10 border-t border-slate-800" data-testid="footer-container">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 pb-16 border-b border-slate-800">
            
            {/* Description Col */}
            <div className="lg:col-span-5 space-y-6 text-left">
              <div className="flex items-center space-x-3">
                <div className="bg-red-900/50 text-amber-400 p-2 rounded-xl border border-red-800">
                  <Brain className="h-5 w-5" />
                </div>
                <span className="font-outfit text-lg sm:text-xl font-extrabold text-white tracking-tight">
                  SCIONFOCUS<span className="text-amber-400">Academy</span>
                </span>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed max-w-sm">
                Empathetic, scientific, and personalized bilingual remedial training support for dyslexia, dysgraphia, ADHD, and speech/concentration challenges in kids. Founded by RATHI MECHERY.
              </p>
              
              <div className="space-y-2 text-xs sm:text-sm text-slate-400">
                <p className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-amber-400 shrink-0" />
                  <a href="tel:+919400650509" className="hover:text-amber-300 smooth-transition" data-testid="footer-phone-link">
                    9400 65 05 09 (Click to Call)
                  </a>
                </p>
                <p className="flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2 text-amber-400 shrink-0" />
                  <a 
                    href="https://wa.me/919810765443" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-amber-300 smooth-transition" 
                    data-testid="footer-whatsapp-link"
                  >
                    WhatsApp Support: Chat Online
                  </a>
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-3 text-left space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-white font-outfit">Quick Navigation</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><a href="#manifesto" className="hover:text-amber-400 smooth-transition">Our Manifesto</a></li>
                <li><a href="#assessment" className="hover:text-amber-400 smooth-transition">Diagnostic Screening</a></li>
                <li><a href="#programs" className="hover:text-amber-400 smooth-transition">Specialisms & Programs</a></li>
                <li><a href="#specialist" className="hover:text-amber-400 smooth-transition">Meet Rathi Mechery</a></li>
                <li><a href="#stories" className="hover:text-amber-400 smooth-transition">Parent Transformations</a></li>
              </ul>
            </div>

            {/* Google Form Link config details card */}
            <div className="lg:col-span-4 bg-slate-900/80 p-6 rounded-2xl border border-slate-800 text-left space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold uppercase tracking-wider text-white font-outfit">Creator Settings</h4>
                <button 
                  onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)}
                  className="text-amber-400 hover:text-amber-300 p-1 rounded smooth-transition"
                  title="Configure Google Form URL"
                >
                  <Settings className="h-4.5 w-4.5" />
                </button>
              </div>
              <p className="text-xs text-slate-300">
                Configure your custom Google Form or CTA targets! Clicking "Save" instantly redirects all call-to-action buttons to your external Google Form.
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="useGoogleFormCheck"
                    checked={useGoogleForm}
                    onChange={(e) => {
                      setUseGoogleForm(e.target.checked);
                      localStorage.setItem("scionfocus_use_google_form", e.target.checked.toString());
                      toast.info(e.target.checked ? "Redirect to Google Form enabled." : "Custom local Booking Modal enabled.");
                    }}
                    className="rounded text-amber-500 focus:ring-amber-500 bg-slate-950 border-slate-850"
                  />
                  <label htmlFor="useGoogleFormCheck" className="text-xs font-semibold text-slate-200 cursor-pointer select-none">
                    Redirect CTAs to Google Form
                  </label>
                </div>
                {useGoogleForm && (
                  <p className="text-[10px] text-amber-400 truncate max-w-[280px]">
                    Active: {googleFormUrl}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between pt-8 text-xs text-slate-400 text-left space-y-4 sm:space-y-0">
            <div>
              <p>© 2026 SCIONFOCUSAcademy. All rights reserved.</p>
              <p className="mt-1 text-slate-500">Certified child specialist and psychologist support in Thrissur, Kerala & Worldwide.</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setIsTermsOpen(true)}
                className="hover:text-amber-400 underline smooth-transition font-medium cursor-pointer"
                data-testid="footer-terms-button"
              >
                Terms & Conditions
              </button>
              <span className="text-slate-700">|</span>
              <span className="text-slate-500">Mango AI funnel builder</span>
            </div>
          </div>
        </div>
      </footer>

      {/* MODAL CONFIGURATION DRAWER */}
      {isConfigPanelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="config-modal-overlay">
          <div className="bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl p-6 sm:p-8 max-w-md w-full mx-4 text-left relative text-white">
            <button 
              onClick={() => setIsConfigPanelOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white p-1 rounded-full smooth-transition"
            >
              <X className="h-5 w-5" />
            </button>

            <form onSubmit={handleSaveConfig} className="space-y-4 pt-2">
              <div className="flex items-center space-x-2 text-white">
                <Settings className="h-5 w-5 text-amber-400" />
                <h3 className="font-outfit text-lg font-bold">Configure CTA Google Form</h3>
              </div>
              
              <p className="text-xs text-slate-400 leading-relaxed">
                Provide your custom Google Form link below, and toggle redirects. All buttons like "Book consultation" and "Enquire" will direct parents there.
              </p>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-300 block">Google Form URL</label>
                <input 
                  type="url"
                  required
                  placeholder="https://docs.google.com/forms/..."
                  value={googleFormUrl}
                  onChange={(e) => setGoogleFormUrl(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                />
              </div>

              <div className="flex items-center space-x-2 py-1">
                <input 
                  type="checkbox"
                  id="modalUseGoogleForm"
                  checked={useGoogleForm}
                  onChange={(e) => setUseGoogleForm(e.target.checked)}
                  className="rounded text-amber-500 focus:ring-amber-500 bg-slate-950 border-slate-800"
                />
                <label htmlFor="modalUseGoogleForm" className="text-xs font-bold text-slate-300 cursor-pointer">
                  Activate Google Form redirection
                </label>
              </div>

              <div className="flex space-x-3 pt-2">
                <button 
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-2.5 rounded-xl text-xs sm:text-sm shadow active:scale-95 smooth-transition"
                >
                  Save Settings
                </button>
                <button 
                  type="button"
                  onClick={() => setIsConfigPanelOpen(false)}
                  className="flex-1 bg-slate-850 hover:bg-slate-800 text-slate-300 font-bold py-2.5 rounded-xl text-xs sm:text-sm smooth-transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 1-ON-1 SLOT BOOKING OR ENQUIRY MODAL */}
      {isBookingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="booking-modal-overlay">
          <div className="bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl p-6 sm:p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto text-left relative text-white">
            
            <button 
              onClick={() => setIsBookingOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white p-1 rounded-full smooth-transition"
              data-testid="modal-close-button"
            >
              <X className="h-5 w-5" />
            </button>

            {bookingSuccess ? (
              <div className="py-8 text-center space-y-5" data-testid="booking-success-message">
                <div className="h-16 w-16 bg-amber-500/10 text-amber-400 rounded-full flex items-center justify-center mx-auto shadow">
                  <CheckCircle2 className="h-10 w-10 text-amber-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-outfit text-2xl font-bold text-white">Appointment Slot Confirmed!</h3>
                  <p className="text-sm font-medium text-amber-400">കുട്ടിയുടെ ഫ്രീ കൗൺസിലിങ് ഉറപ്പാക്കിയിട്ടുണ്ട്.</p>
                  <p className="text-sm text-slate-300 max-w-md mx-auto pt-2">
                    Thank you {submittedBooking?.parentName || bookingForm.parentName}. We have saved your 45-minute diagnostic slot on <strong>{submittedBooking?.date || bookingForm.date}</strong> at <strong>{submittedBooking?.timeSlot || bookingForm.timeSlot}</strong> for <strong>{submittedBooking?.childName || bookingForm.childName}</strong>. 
                  </p>
                  <p className="text-xs text-slate-400 pt-1">
                    Specialist Rathi Mechery will call you on <strong>{submittedBooking?.phone || bookingForm.phone}</strong>.
                  </p>
                </div>
                <div className="pt-4">
                  <button 
                    onClick={() => {
                      setBookingSuccess(false);
                      setIsBookingOpen(false);
                      setBookingForm({
                        childName: "",
                        childAge: "",
                        parentName: "",
                        phone: "",
                        challenges: [],
                        date: "",
                        timeSlot: "",
                        concerns: ""
                      });
                    }}
                    className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold px-8 py-3 rounded-xl smooth-transition"
                  >
                    Done & Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6 pt-2">
                <div>
                  <h3 className="font-outfit text-2xl font-bold text-white">
                    {selectedProgram ? `Enquire: ${selectedProgram}` : "Book empathetic 1-on-1 Consultation"}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-400 mt-1">
                    We will call you to confirm your child's free development diagnostic screening session.
                  </p>
                </div>

                <form onSubmit={handleBookingSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-300">Child's Name (കുട്ടിയുടെ പേര്) *</label>
                      <input 
                        type="text" 
                        required
                        placeholder="Eg. Rohan"
                        value={bookingForm.childName}
                        onChange={(e) => setBookingForm(prev => ({ ...prev, childName: e.target.value }))}
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="modal-input-child-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-300">Child's Age (കുട്ടിയുടെ പ്രായം) *</label>
                      <input 
                        type="number" 
                        required
                        placeholder="Eg. 7"
                        value={bookingForm.childAge}
                        onChange={(e) => setBookingForm(prev => ({ ...prev, childAge: e.target.value }))}
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="modal-input-child-age"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-300">Parent's Name (രക്ഷിതാവിന്റെ പേര്) *</label>
                      <input 
                        type="text" 
                        required
                        placeholder="Eg. Rajesh Kumar"
                        value={bookingForm.parentName}
                        onChange={(e) => setBookingForm(prev => ({ ...prev, parentName: e.target.value }))}
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="modal-input-parent-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-300">Contact Number (ഫോൺ നമ്പർ) *</label>
                      <input 
                        type="tel" 
                        required
                        placeholder="Eg. 9400650509"
                        value={bookingForm.phone}
                        onChange={(e) => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="modal-input-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 block">Select Challenges</label>
                    <div className="grid grid-cols-3 gap-2">
                      {["Reading", "Writing", "Focus", "Memory", "Speech", "Behavior"].map((chal) => {
                        const isChecked = bookingForm.challenges.includes(chal);
                        return (
                          <button
                            key={chal}
                            type="button"
                            onClick={() => {
                              setBookingForm(prev => ({
                                ...prev,
                                challenges: isChecked 
                                  ? prev.challenges.filter(c => c !== chal)
                                  : [...prev.challenges, chal]
                              }));
                            }}
                            className={`px-2.5 py-1.5 border rounded-xl text-xs font-semibold smooth-transition flex items-center justify-between ${
                              isChecked 
                                ? "border-amber-500 bg-amber-500/10 text-amber-300" 
                                : "border-slate-800 hover:bg-slate-850 text-slate-300"
                            }`}
                            data-testid={`modal-challenge-${chal.toLowerCase()}`}
                          >
                            <span>{chal}</span>
                            {isChecked && <CheckCircle2 className="h-3 w-3 text-amber-500" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-300">Choose Date *</label>
                      <input 
                        type="date" 
                        required
                        value={bookingForm.date}
                        onChange={(e) => setBookingForm(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="modal-input-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-300">Time Slot *</label>
                      <select 
                        required
                        value={bookingForm.timeSlot}
                        onChange={(e) => setBookingForm(prev => ({ ...prev, timeSlot: e.target.value }))}
                        className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition"
                        data-testid="modal-input-time-slot"
                      >
                        <option value="">Select a slot</option>
                        <option value="10:00 AM - 10:45 AM">10:00 AM - 10:45 AM (Morning)</option>
                        <option value="11:30 AM - 12:15 PM">11:30 AM - 12:15 PM (Morning)</option>
                        <option value="03:00 PM - 03:45 PM">03:00 PM - 03:45 PM (Afternoon)</option>
                        <option value="05:30 PM - 06:15 PM">05:30 PM - 06:15 PM (Evening)</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-300">Describe specific concerns (മറ്റ് വിവരങ്ങൾ)</label>
                    <textarea 
                      rows="2.5"
                      placeholder="Share what issues child faces..."
                      value={bookingForm.concerns}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, concerns: e.target.value }))}
                      className="w-full px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 smooth-transition resize-none"
                      data-testid="modal-input-concerns"
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-3 px-5 rounded-xl text-center shadow active:scale-95 smooth-transition"
                    data-testid="modal-submit-button"
                  >
                    Confirm Appointment Slot (ഫ്രീ കൗൺസിലിങ് ഉറപ്പാക്കുക)
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TERMS & CONDITIONS MODAL */}
      {isTermsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="terms-modal-overlay">
          <div className="bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl p-6 sm:p-8 max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col text-left relative text-white">
            
            <button 
              onClick={() => setIsTermsOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 smooth-transition"
              data-testid="terms-modal-close"
              aria-label="Close Terms"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center space-x-3 pb-4 border-b border-slate-800">
              <div className="bg-amber-500/10 text-amber-400 p-2 rounded-xl">
                <Brain className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-outfit text-xl font-extrabold text-white">Terms & Conditions</h3>
                <p className="text-xs text-slate-400">SCIONFOCUSAcademy • Rathi Mechery</p>
              </div>
            </div>

            {/* Scrollable Terms Text */}
            <div className="overflow-y-auto pr-2 my-6 space-y-6 text-sm text-slate-300 leading-relaxed font-dmsans scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
              <div>
                <h4 className="font-outfit font-bold text-white text-base mb-2">1. Scope of Support Services</h4>
                <p>
                  SCIONFOCUSAcademy, founded by counseling psychologist Rathi Mechery, offers expert child-centric developmental guidance, cognitive and remedial coaching, reading/writing/focus support, and parent mentoring for kids aged 3–15. All sessions are strictly focused on child development and learning support.
                </p>
              </div>

              <div>
                <h4 className="font-outfit font-bold text-white text-base mb-2">2. Professional & Medical Disclaimer</h4>
                <p>
                  Our remedial consultation, diagnostic screening quiz, and learning plans are designed as developmental counseling and educational support. While guided by Rathi Mechery (M.Phil, Counseling Psychologist), these sessions and online screening results are not a substitute for clinical medical treatments, immediate pediatric emergency attention, or clinical psychiatric prescriptions.
                </p>
              </div>

              <div>
                <h4 className="font-outfit font-bold text-white text-base mb-2">3. Quiz Assessments & Data Privacy</h4>
                <p>
                  We treat parental reports, screening responses, diagnostic quiz choices, and phone coordinates with the highest standards of ethics and strict professional confidentiality. We will never share your child's developmental assessment results with outside marketing agencies or unauthorized third parties.
                </p>
              </div>

              <div>
                <h4 className="font-outfit font-bold text-white text-base mb-2">4. Booking, Rescheduling & Cancellation</h4>
                <p>
                  To provide consistent support to all families in Kerala and beyond, we request parents to respect scheduled time slots. If you need to reschedule or cancel a booked slot, please notify us at least 24 hours in advance via call or WhatsApp at <strong className="text-amber-400">+91 94006 50509</strong>.
                </p>
              </div>

              <div>
                <h4 className="font-outfit font-bold text-white text-base mb-2">5. Bilingual Communication Agreement</h4>
                <p>
                  Support materials, consultation sessions, and quiz diagnostics are provided in English, Malayalam, or bilingually as preferred by the family to facilitate maximum comfort and effective support for the child.
                </p>
              </div>

              <div>
                <h4 className="font-outfit font-bold text-white text-base mb-2">6. Agreement Acceptance</h4>
                <p>
                  By utilizing this website, participating in our online diagnostic screening, or scheduling consultation sessions with Rathi Mechery, you fully accept and agree to these Terms & Conditions.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800 flex justify-end">
              <button 
                onClick={() => setIsTermsOpen(false)}
                className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-extrabold py-2.5 px-6 rounded-xl text-sm shadow active:scale-95 smooth-transition"
                data-testid="terms-modal-accept"
              >
                I Understand & Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
